import { Injectable, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SvgIconRegistryService } from 'angular-svg-icon';
import { forkJoin, from, of } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { LoggerService } from './logger.service';
import { MenuService } from './menu.service';
import { AuthService } from './auth.service';

/**
 * SVG Icon Preloader Service (DEV-2060 / FE-PERF-001)
 *
 * Pre-registers all sidebar menu icons into the SvgIconRegistryService
 * during app initialization. This ensures SVG icons are fetched exactly
 * once and served from the in-memory cache on all subsequent renders,
 * eliminating duplicate HTTP requests on navigation.
 *
 * RF-QA-174 · The registry (`angular-svg-icon`) already keys its cache by
 * URL and shares an in-flight request between concurrent `<svg-icon>`s, so
 * every distinct `/assets/icons/heroicons/**.svg` is fetched at most once per
 * session. What this service controls is WHEN and WHICH files are warmed:
 *   - it now warms exactly the icons of the VISIBLE menu (what the sidebar
 *     will render) instead of every registered page — the previous list also
 *     carried six layout icons no template references any more;
 *   - it is triggered by the authenticated, permission-hydrated session
 *     (see {@link preloadWhenAuthenticated}) rather than a bootstrap-time
 *     localStorage flag, so a cold login is warmed in one batch too.
 */
@Injectable({ providedIn: 'root' })
export class SvgIconPreloaderService {
  private readonly iconRegistry = inject(SvgIconRegistryService);
  private readonly menuService = inject(MenuService);
  private readonly logger = inject(LoggerService);
  private readonly auth = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Collect the unique icon paths of the menu the current principal can see.
   * Icons of permission-filtered entries are NOT requested; if permissions
   * change mid-session the sidebar's own `<svg-icon>` loads them on demand.
   */
  private collectIconPaths(): string[] {
    const paths = new Set<string>();

    for (const group of this.menuService.visibleMenu()) {
      for (const item of group.items) {
        if (item.icon) {
          paths.add(item.icon);
        }
        if (item.children) {
          for (const child of item.children) {
            if (child.icon) {
              paths.add(child.icon);
            }
          }
        }
      }
    }

    return Array.from(paths);
  }

  /**
   * RF-QA-174 · Warm the registry once the session is authenticated AND its
   * effective permissions are hydrated (so `visibleMenu()` is final). Runs at
   * most once per service lifetime; never blocks bootstrap.
   */
  preloadWhenAuthenticated(): void {
    this.auth.isAuthenticated$
      .pipe(
        filter((authenticated) => authenticated),
        take(1),
        switchMap(() => from(this.auth.whenAuthReady())),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        // Non-blocking: the returned Promise is intentionally not awaited.
        void this.preloadIcons();
      });
  }

  /**
   * Pre-load all icon SVGs into the registry cache.
   * Returns a Promise that resolves when all icons are loaded.
   */
  preloadIcons(): Promise<void> {
    const iconPaths = this.collectIconPaths();

    if (iconPaths.length === 0) {
      return Promise.resolve();
    }

    this.logger.debug(`Preloading ${iconPaths.length} SVG icons`, {
      component: 'SvgIconPreloader',
    });

    const loadObservables = iconPaths.map((path) => {
      const obs = this.iconRegistry.loadSvg(path);
      if (!obs) {
        return of(undefined);
      }
      return obs.pipe(
        take(1),
        catchError((err) => {
          this.logger.warn(`Failed to preload SVG icon: ${path}`, {
            component: 'SvgIconPreloader',
          }, err);
          return of(undefined);
        })
      );
    });

    return new Promise<void>((resolve) => {
      forkJoin(loadObservables).subscribe({
        next: () => {
          this.logger.debug(
            `Successfully preloaded ${iconPaths.length} SVG icons`,
            { component: 'SvgIconPreloader' }
          );
          resolve();
        },
        error: () => {
          // Resolve anyway -- icon preloading should not block app startup
          this.logger.warn('SVG icon preloading completed with errors', {
            component: 'SvgIconPreloader',
          });
          resolve();
        },
      });
    });
  }
}
